import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDataChangeDetection } from '@/hooks/useDataChangeDetection';
import { Wrapper } from '../wrapper.styles';
import { FormGroupComponent } from '@components/input/FormGroup/FormGroup';
import { PhoneInput } from '@/components/input/PhoneInput/PhoneInput';
import { TextInput } from '../../components/input/TextInput/TextInput';
import { TextBlock } from '@components/text/TextBlock';
import { TypeOfText } from '@components/text/TextBlock';
import { DropDownInput } from '@/components/input/DropDownInput/DropDownInput';
import DataPicker from '@/components/input/DatePicker/DatePicker';
import { useAppSelector, useAppDispatch } from '@app/hooks';
import { CheckConsent } from '@/components/input/CheckConsent/CheckConsent';
import { NavigationButton, PageOrigin } from '@/components/Button/NavigationButton/NavigationButton';
import { useIsMobile } from '@/hooks/useIsMobile';
import { PersonalInfoWrapper, ButtonWrapper, ConfirmMessageContainer } from './PersonalInformation.styles';
import { Button } from '@/components/Button/DefaultButton/Button';
import _ from 'lodash';
import { fieldFilled } from '@/utils/handleFieldsFilled';
import { LoanApplicationState } from '@/reducers/LoanApplication.types';
import { pages } from '@/defaultVerbiages';
import { ConfirmationModal } from '@/components/Modals/ConfirmModal/ConfirmModal';

interface PersonalInformationProps {
  onAccess: (page: string) => void;
}

type Borrower = {
  selectStatus: any;
  dateBirthday: string | undefined;
  SSN: any;
  contactMethod: any;
  citizenship: any;
  mobile?: string;
  homePhone?: string;
  workPhone?: string;
};

type CoBorrower = {
  firstName: string;
  lastName: string;
  relationship: string;
  selectStatusCoborrower: any;
  dateBirthdayCoBorrower: string | undefined;
  mobilePhoneCoBorrower?: string;
  email: string;
  citizenshipCoborrower: any;
  contactMethodCoBorrower: any;
  SSNCoBorrower: any;
  workPhoneCoBorrower?: string;
  homePhoneCoBorrower?: string;
  suffixCoBorrower?: string;
};

export enum PropertyType {
  Status = 'selectStatus',
  DateBirthday = 'dateBirthday',
  SSN = 'SSN',
  MobilePhone = 'mobilePhone',
  HomePhone = 'homePhone',
  WorkPhone = 'workPhone',
  ContactMethod = 'contactMethod',
  Citizenship = 'citizenship',
  MailAddress = 'emailAddress',
  FirstNameCoBorrower = 'firstNameCoBorrower',
  MiddleNameCoBorrower = 'middleNameCoBorrower',
  LastNameCoborrower = 'lastNameCoborrower',
  SuffixCoborrower = 'suffixCoborrower',
  Relationship = 'relationship',
  StatusCoBorrower = 'selectStatusCoborrower',
  DateBirthdayCoBorrower = 'dateBirthdayCoBorrower',
  MobilePhoneCoBorrower = 'mobilePhoneCoBorrower',
  HomePhoneCoBorrower = 'homePhoneCoBorrower',
  WorkPhoneCoBorrower = 'workPhoneCoBorrower',
  EmailCoborrower = 'emailCoborrower',
  CitizenshipCoborrower = 'citizenshipCoborrower',
  ContactMethodCoBorrower = 'contactMethodCoBorrower',
  SSNCoBorrower = 'SSNCoBorrower',
}
const PersonalInformation: React.FC<PersonalInformationProps> = ({ onAccess }) => {
  const [coBorrower, setCoBorrower] = useState<number>(0);
  const [, setBorrowerInfo] = useState({ date: '', email: '' });
  let loanApplication = useAppSelector((state) => state.LoanApplication);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const dispatch = useAppDispatch();
  let templateConfig = useAppSelector((state) => state.TemplateConfig);
  const normalizeBeValue = (input: string | undefined | null): string => {
    const options = templateConfig?.pages?.personalInformation?.formInputs?.Coborrower?.citizenship?.options ?? [];
    const match = options.find((opt) => opt.label === input || opt.beValue === input);
    return match?.beValue ?? '';
  };
  const [showWarningSSNModal, setShowWarningSSNModal] = useState(false);
  const [hasUserConfirmedSSNChange, setHasUserConfirmedSSNChange] = useState(false);
  const [showBackAfirmationModal, setShowBackAfirmationModal] = useState(false);
  const {
    selectStatus,
    SSN,
    workPhone,
    mobile,
    mobilePhone,
    homePhone,
    contactMethod,
    citizenship,
    emailAddress,
    firstNameCoBorrower,
    middleNameCoBorrower,
    lastNameCoborrower,
    suffixCoborrower,
    relationship,
    selectStatusCoborrower,
    dateBirthdayCoBorrower,
    mobilePhoneCoBorrower,
    homePhoneCoBorrower,
    workPhoneCoBorrower,
    citizenshipCoborrower,
    emailCoborrower,
    contactMethodCoBorrower,
    SSNCoBorrower,
    TCPA,
    phone,
    selectedPhoneType,
  } = useAppSelector((state) => state.CurrentForm);
  const hasCoBorrower = coBorrower !== 0 || loanApplication?.borrowers?.length > 1;
  const hasDrawAmountPage = templateConfig?.pages?.hasDrawAmountPage ?? false;
  const pageNavigation = hasDrawAmountPage ? PageOrigin.VMO_DRAW_AMOUNT : PageOrigin.VMO_LINE_AMOUNT;
  const previousPage = templateConfig?.features?.vmoRedesign?.isEnabled ? PageOrigin.VMO : pageNavigation;
  const isLoanDepot = templateConfig?.lenderName === 'loanDepot';
  const hasDateBirthday = loanApplication?.borrowers[0]?.borrowerDOB !== '';
  const TCPARequired = templateConfig?.pages?.personalInformation?.checkConsent?.isRequired !== false;
  const [isValidDateOfBirth, setIsValidDateOfBirth] = useState(!!loanApplication?.borrowers[0]?.borrowerDOB);
  const initialCitizenship = normalizeBeValue(citizenshipCoborrower ?? loanApplication?.borrowers[1]?.borrowerCitizenship) ?? '';
  // Helper function to get the correct phone value for each input
  const getPhoneValueForType = (phoneType: 'mobilePhone' | 'homePhone' | 'workPhone') => {
    // If this phone type was selected in GetStarted, try to get the value
    if (selectedPhoneType === phoneType) {
      // First try the specific phone type field from CurrentForm
      const specificPhoneValue = currentFormState[phoneType];
      if (specificPhoneValue) {
        return specificPhoneValue;
      }
      // Fallback to the generic phone field
      if (phone) {
        return phone;
      }
    }
    // Otherwise, return empty string or fallback to loanApplication data
    return '';
  };

  const currentFormState = useAppSelector((state) => state.CurrentForm);

  const [formValues, setFormValues] = useState({
    selectStatus: selectStatus ?? loanApplication?.borrowers[0]?.borrowerMaritalStatus ?? '',
    dateBirthday: loanApplication?.borrowers[0]?.borrowerDOB ?? '',
    SSN: SSN ?? loanApplication?.borrowers[0]?.borrowerSSN ?? '',
    mobile: mobile ?? loanApplication?.borrowers[0]?.borrowerPrimaryPhone ?? '',
    mobilePhone: getPhoneValueForType('mobilePhone') || mobilePhone || loanApplication?.borrowers[0]?.borrowerPrimaryPhone || '',
    homePhone: getPhoneValueForType('homePhone') || homePhone || loanApplication?.borrowers[0]?.borrowerAlternatePhone || '',
    workPhone: getPhoneValueForType('workPhone') || workPhone || loanApplication?.borrowers[0]?.borrowerWorkPhone || '',
    contactMethod: contactMethod ?? loanApplication?.borrowers[0]?.borrowerPreferredContactMethod ?? '',
    citizenship: citizenship ?? loanApplication?.borrowers[0]?.borrowerCitizenship ?? '',
    emailAddress: emailAddress ?? loanApplication?.borrowers[0]?.borrowerEmailAddress ?? '',
    firstNameCoBorrower: firstNameCoBorrower ?? loanApplication?.borrowers[1]?.borrowerFirstName ?? '',
    middleNameCoBorrower: middleNameCoBorrower ?? loanApplication?.borrowers[1]?.borrowerMiddleName ?? '',
    lastNameCoborrower: lastNameCoborrower ?? loanApplication?.borrowers[1]?.borrowerLastName ?? '',
    suffixCoborrower: suffixCoborrower ?? loanApplication?.borrowers[1]?.borrowerSuffixName ?? '',
    relationship: relationship ?? (loanApplication?.borrowers[1]?.isSpouse ? 'Spouse' : ''),
    selectStatusCoborrower: selectStatusCoborrower ?? loanApplication?.borrowers[1]?.borrowerMaritalStatus ?? '',
    dateBirthdayCoBorrower: dateBirthdayCoBorrower ?? loanApplication?.borrowers[1]?.borrowerDOB ?? '',
    mobilePhoneCoBorrower: mobilePhoneCoBorrower ?? loanApplication?.borrowers[1]?.borrowerPrimaryPhone ?? '',
    homePhoneCoBorrower: homePhoneCoBorrower ?? loanApplication?.borrowers[1]?.borrowerAlternatePhone ?? '',
    workPhoneCoBorrower: workPhoneCoBorrower ?? loanApplication?.borrowers[1]?.borrowerWorkPhone ?? '',
    citizenshipCoborrower: initialCitizenship,
    emailCoborrower: emailCoborrower ?? loanApplication?.borrowers[1]?.borrowerEmailAddress ?? '',
    contactMethodCoBorrower: contactMethodCoBorrower ?? loanApplication?.borrowers[1]?.borrowerPreferredContactMethod ?? '',
    SSNCoBorrower: SSNCoBorrower ?? loanApplication?.borrowers[1]?.borrowerSSN ?? '',
    TCPA: TCPA ?? loanApplication?.isTcpaAccepted ?? false,
    coapplicantConsent: loanApplication?.consent?.eConsentCoborrower || false,
  });

  const isSSNValid = !hasCoBorrower || formValues.SSNCoBorrower !== formValues.SSN;

  const [formError, setFormError] = useState(false);
  const [dateOfBirthError, setDateOfBirthError] = useState(false);
  const [phoneError, setPhoneError] = useState(false);
  const [SNNError, setSNNError] = useState(false);
  const [dateOfBirthCoBorrowerError, setDateOfBirthCoBorrowerError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const coApplicantConsent = loanApplication?.consent?.eConsentCoborrower || formValues.coapplicantConsent;
  const { hasDataChanged, resetChangeDetection } = useDataChangeDetection([
    'maritalStatus',
    'dateBirthday',
    'SSN',
    'mobilePhone',
    'homePhone',
    'workPhone',
    'contactMethod',
    'citizenship',
  ]);

  const validateSSN = (value: string): boolean => {
    const cleanedValue = value.replace(/\D/g, '');
    return cleanedValue.length === 9;
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const handleSSNChange = (value: string, propertyType: PropertyType) => {
    if (!hasUserConfirmedSSNChange) {
      return;
    }

    const isValid = validateSSN(value);
    handleChange(value, propertyType);
    if (!isValid) {
      setSNNError(true);
    }
  };

  const handleBackModalResponse = (confirmed: boolean) => {
    setShowWarningSSNModal(false);
    setHasUserConfirmedSSNChange(confirmed);
  };

  const handleChange = (value: string | number | boolean, name: string) => {
    setFormValues((prevValues) => {
      const newValues = {
        ...prevValues,
        [name]: value,
      };
      dispatch({ type: 'CurrentForm/addField', payload: newValues });
      return newValues;
    });
  };

  const handleBackResponse = (confirmed: boolean) => {
    setShowBackAfirmationModal(false);
    if (confirmed) {
      //VMO V2
      if (templateConfig?.features?.vmoRedesign?.isEnabled) {
        navigate(`/${PageOrigin.VMO}`);
      } else {
        // VMO V1
        const hasDrawAmountPage = templateConfig?.pages?.hasDrawAmountPage ?? false;
        const backToPage = hasDrawAmountPage ? PageOrigin.VMO_DRAW_AMOUNT : PageOrigin.VMO_LINE_AMOUNT;
        navigate(`/${backToPage}`);
      }
    }
  };

  const requiredValues = {
    borrower: {
      selectStatus: formValues.selectStatus,
      dateBirthday: hasDateBirthday ? loanApplication?.borrowers[0]?.borrowerDOB : formValues.dateBirthday,
      SSN: formValues.SSN,
      contactMethod: formValues.contactMethod,
      citizenship: formValues.citizenship,
      ...(TCPARequired && { TCPA: formValues.TCPA }),
    } as Borrower,
    coBorrower: {
      firstName: formValues.firstNameCoBorrower,
      lastName: formValues.lastNameCoborrower,
      relationship: formValues.relationship,
      selectStatusCoborrower: formValues.selectStatusCoborrower,
      dateBirthdayCoBorrower: formValues.dateBirthdayCoBorrower,
      email: formValues.emailCoborrower,
      citizenshipCoborrower: formValues.citizenshipCoborrower,
      contactMethodCoBorrower: formValues.contactMethodCoBorrower,
      SSNCoBorrower: formValues.SSNCoBorrower,
    } as CoBorrower,
  };
  const coApplicantLabel = templateConfig.pages?.personalInformation?.formInputs?.Coborrower?.coApplicantLabel || 'Co-Borrower';
  const coBorrowerPhoneRequired = templateConfig?.pages?.personalInformation?.formInputs?.Coborrower;

  if (coBorrowerPhoneRequired?.mobilePhone?.isRequired || formValues.contactMethodCoBorrower === 'cell') {
    requiredValues.coBorrower.mobilePhoneCoBorrower = formValues.mobilePhoneCoBorrower;
  }
  if (coBorrowerPhoneRequired?.homePhone?.isRequired || formValues.contactMethodCoBorrower === 'home') {
    requiredValues.coBorrower.homePhoneCoBorrower = formValues.homePhoneCoBorrower;
  }
  if (coBorrowerPhoneRequired?.workPhone?.isRequired || formValues.contactMethodCoBorrower === 'work') {
    requiredValues.coBorrower.workPhoneCoBorrower = formValues.workPhoneCoBorrower;
  }
  const borrowerPhoneRequired = templateConfig?.pages?.personalInformation?.formInputs;
  if (borrowerPhoneRequired?.mobilePhone?.isRequired || formValues.contactMethod === 'cell') {
    requiredValues.borrower.mobile = formValues.mobilePhone || formValues.mobile;
  }
  if (borrowerPhoneRequired?.homePhone?.isRequired || formValues.contactMethod === 'home') {
    requiredValues.borrower.homePhone = formValues.homePhone;
  }
  if (borrowerPhoneRequired?.workPhone?.isRequired || formValues.contactMethod === 'work') {
    requiredValues.borrower.workPhone = formValues.workPhone;
  }

  const allRequiredValues = hasCoBorrower ? { ...requiredValues.borrower, ...requiredValues.coBorrower } : requiredValues.borrower;

  useEffect(() => {
    const beValue = normalizeBeValue(formValues.citizenshipCoborrower);
    if (formValues.citizenshipCoborrower !== beValue) {
      setFormValues((prev) => ({
        ...prev,
        citizenshipCoborrower: beValue,
      }));
      dispatch({
        type: 'CurrentForm/addField',
        payload: {
          ...formValues,
          citizenshipCoborrower: beValue,
        },
      });
    }
  }, [formValues.citizenshipCoborrower]);

  useEffect(() => {
    const allFieldsFilled = fieldFilled(allRequiredValues);
    const isEmailValid = !hasCoBorrower || (formValues.emailCoborrower !== formValues.emailAddress && validateEmail(formValues.emailCoborrower));
    const isSSNValid = !hasCoBorrower || formValues.SSNCoBorrower !== formValues.SSN;
    const isBorrowerSSNValid = validateSSN(formValues.SSN);
    const isCoBorrowerSSNValid = !hasCoBorrower || validateSSN(formValues.SSNCoBorrower);

    const hasBorrowerEmailValidationError = formValues.emailAddress && !validateEmail(formValues.emailAddress);
    const hasCoBorrowerEmailValidationError = hasCoBorrower && formValues.emailCoborrower && !validateEmail(formValues.emailCoborrower);
    const hasBorrowerSSNValidationError = formValues.SSN && !validateSSN(formValues.SSN);
    const hasCoBorrowerSSNValidationError = hasCoBorrower && formValues.SSNCoBorrower && !validateSSN(formValues.SSNCoBorrower);

    const phoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/;
    const hasBorrowerMobilePhoneValidationError = formValues.mobilePhone && !phoneRegex.test(formValues.mobilePhone);
    const hasBorrowerHomePhoneValidationError = formValues.homePhone && !phoneRegex.test(formValues.homePhone);
    const hasBorrowerWorkPhoneValidationError = formValues.workPhone && !phoneRegex.test(formValues.workPhone);
    const hasCoBorrowerMobilePhoneValidationError = hasCoBorrower && formValues.mobilePhoneCoBorrower && !phoneRegex.test(formValues.mobilePhoneCoBorrower);
    const hasCoBorrowerHomePhoneValidationError = hasCoBorrower && formValues.homePhoneCoBorrower && !phoneRegex.test(formValues.homePhoneCoBorrower);
    const hasCoBorrowerWorkPhoneValidationError = hasCoBorrower && formValues.workPhoneCoBorrower && !phoneRegex.test(formValues.workPhoneCoBorrower);

    const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
    const hasBorrowerDateOfBirthValidationError = formValues.dateBirthday && formValues.dateBirthday.trim() !== '' && !dateRegex.test(formValues.dateBirthday);
    const hasCoBorrowerDateOfBirthValidationError =
      hasCoBorrower && formValues.dateBirthdayCoBorrower && formValues.dateBirthdayCoBorrower.trim() !== '' && !dateRegex.test(formValues.dateBirthdayCoBorrower);

    const hasValidationErrors =
      hasBorrowerEmailValidationError ||
      hasCoBorrowerEmailValidationError ||
      hasBorrowerSSNValidationError ||
      hasCoBorrowerSSNValidationError ||
      hasBorrowerMobilePhoneValidationError ||
      hasBorrowerHomePhoneValidationError ||
      hasBorrowerWorkPhoneValidationError ||
      hasCoBorrowerMobilePhoneValidationError ||
      hasCoBorrowerHomePhoneValidationError ||
      hasCoBorrowerWorkPhoneValidationError ||
      hasBorrowerDateOfBirthValidationError ||
      hasCoBorrowerDateOfBirthValidationError;

    const isFormValid = allFieldsFilled && isEmailValid && isSSNValid && isBorrowerSSNValid && isCoBorrowerSSNValid && !hasValidationErrors;

    setSNNError(!isSSNValid || hasBorrowerSSNValidationError || hasCoBorrowerSSNValidationError);
    setEmailError(!isEmailValid || hasBorrowerEmailValidationError || hasCoBorrowerEmailValidationError);
    setPhoneError(
      hasBorrowerMobilePhoneValidationError ||
        hasBorrowerHomePhoneValidationError ||
        hasBorrowerWorkPhoneValidationError ||
        hasCoBorrowerMobilePhoneValidationError ||
        hasCoBorrowerHomePhoneValidationError ||
        hasCoBorrowerWorkPhoneValidationError,
    );
    setDateOfBirthError(!!hasBorrowerDateOfBirthValidationError);
    setDateOfBirthCoBorrowerError(!!hasCoBorrowerDateOfBirthValidationError);
    setFormError(!isFormValid);
  }, [formValues, hasCoBorrower]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (loanApplication?.borrowers[0]?.borrowerDOB) {
      setBorrowerInfo({ date: loanApplication?.borrowers[0]?.borrowerDOB, email: loanApplication?.borrowers[0]?.borrowerEmailAddress });
    }
  }, [loanApplication]);

  useEffect(() => {
    if (loanApplication?.borrowers[0]?.borrowerDOB) {
      setIsValidDateOfBirth(true);
    }
  }, [loanApplication?.borrowers[0]?.borrowerDOB]);

  useEffect(() => {
    if (loanApplication?.borrowers[1]) {
      setFormValues((prev) => ({
        ...prev,
        dateBirthdayCoBorrower: loanApplication.borrowers[1].borrowerDOB || prev.dateBirthdayCoBorrower,
      }));
    }
  }, [loanApplication?.borrowers[1]?.borrowerDOB]);

  const [employmentHistoryCoBorrower, setEmploymentHistoryCoBorrower] = useState<
    {
      appendValue: number;
      type: string;
      employerName: string;
      jobTitle: string;
      startDate: string;
      endDate: string;
      grossMonthlyIncome: number;
      currentEmployment: boolean | null;
      totalMonths?: undefined | number;
    }[]
  >([
    {
      appendValue: loanApplication?.borrowers[1]?.appendValue || 0,
      type: loanApplication?.borrowers[1]?.employmentHistory[0]?.type || '',
      employerName: loanApplication?.borrowers[1]?.employmentHistory[0]?.employerName || '',
      jobTitle: loanApplication?.borrowers[1]?.employmentHistory[0]?.jobTitle || '',
      startDate: loanApplication?.borrowers[1]?.employmentHistory[0]?.startDate || '',
      endDate: loanApplication?.borrowers[1]?.employmentHistory[0]?.endDate || '',
      grossMonthlyIncome: loanApplication?.borrowers[1]?.employmentHistory[0]?.grossMonthlyIncome || 0,
      currentEmployment: loanApplication?.borrowers[1]?.employmentHistory[0]?.currentEmployment || null,
      totalMonths: loanApplication?.borrowers[1]?.employmentHistory[0]?.totalMonths || 0,
    },
  ]);

  useEffect(() => {
    if (loanApplication?.borrowers[1]?.employmentHistory) {
      setEmploymentHistoryCoBorrower(loanApplication?.borrowers[1]?.employmentHistory);
    }
  }, [loanApplication?.borrowers[1]]);

  useEffect(() => {
    let auxLoanApplication: LoanApplicationState = _.cloneDeep(loanApplication);
    const updatedBorrower = {
      ...auxLoanApplication.borrowers[0],
      borrowerPrimaryPhone: formValues.mobilePhone || formValues.mobile,
      borrowerAlternatePhone: formValues.homePhone,
      borrowerWorkPhone: formValues.workPhone,
      ...(formValues.citizenship && { borrowerCitizenship: formValues.citizenship }),
      borrowerMaritalStatus: formValues.selectStatus || '',
      borrowerPreferredContactMethod: formValues.contactMethod,
      borrowerSSN: formValues.SSN,
      ...(formValues.dateBirthday && { borrowerDOB: formValues.dateBirthday }),
    };

    if (coBorrower > 0) {
      const updatedCoBorrower = {
        ...auxLoanApplication.borrowers[1],
        borrowerFirstName: formValues.firstNameCoBorrower,
        borrowerMiddleName: formValues.middleNameCoBorrower,
        borrowerLastName: formValues.lastNameCoborrower,
        ...(formValues.suffixCoborrower && { borrowerSuffixName: formValues.suffixCoborrower }),
        borrowerRelationship: formValues.relationship,
        borrowerMaritalStatus: formValues.selectStatusCoborrower || '',
        ...(formValues.dateBirthdayCoBorrower && { borrowerDOB: formValues.dateBirthdayCoBorrower }),
        borrowerEmailAddress: formValues.emailCoborrower,
        borrowerPrimaryPhone: formValues.mobilePhoneCoBorrower,
        borrowerAlternatePhone: formValues.homePhoneCoBorrower,
        ...(formValues.citizenshipCoborrower && { borrowerCitizenship: formValues.citizenshipCoborrower }),
        borrowerWorkPhone: formValues.workPhoneCoBorrower,
        borrowerPreferredContactMethod: formValues.contactMethodCoBorrower,
        borrowerSSN: formValues.SSNCoBorrower,
        appendValue: loanApplication?.borrowers[1]?.appendValue || 0,
        isPrimary: false,
        isSpouse: formValues.relationship === 'Spouse',
        employmentHistory: employmentHistoryCoBorrower,
        governmentHMDA: {
          applicantRace: loanApplication?.borrowers[1]?.governmentHMDA?.applicantRace || [],
          applicantEthnicity: loanApplication?.borrowers[1]?.governmentHMDA?.applicantEthnicity || [],
          applicantSex: loanApplication?.borrowers[1]?.governmentHMDA?.applicantSex || [],
        },
        otherIncome: loanApplication?.borrowers[1]?.otherIncome || [],
      };

      auxLoanApplication.borrowers[1] = updatedCoBorrower;
      auxLoanApplication.consent.eConsentCoborrower = formValues.coapplicantConsent;
    } else if (coBorrower === 0 && auxLoanApplication.borrowers?.length > 1) {
      auxLoanApplication.borrowers.splice(1, 1);
      auxLoanApplication.consent.eConsentCoborrower = false;
    }

    auxLoanApplication.isTcpaAccepted = formValues.TCPA;
    auxLoanApplication.consent.tcpaConsent = formValues.TCPA;
    auxLoanApplication.borrowers[0] = updatedBorrower;
    dispatch({
      type: 'LoanApplication/updateValue',
      payload: { ...auxLoanApplication },
    });
  }, [formValues, coBorrower]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleIsRequired = (value: 'cell' | 'home' | 'work' | 'Email', type: 'borrower' | 'coBorrower'): boolean => {
    const contactMethod = type === 'borrower' ? formValues?.contactMethod : formValues?.contactMethodCoBorrower;
    return (value === 'cell' && isLoanDepot) || value.toLowerCase() === contactMethod.toLowerCase();
  };

  const { personalInformation } = pages;
  const templatePersonalInformation = templateConfig?.pages?.personalInformation;

  const handleNavigation = (page: string) => {
    if (!formError && isValidDateOfBirth && isSSNValid) {
      onAccess(page);
    }
  };

  useEffect(() => {
    if (loanApplication?.borrowers?.length > 1) {
      setCoBorrower(1);
    }
  }, [loanApplication?.borrowers]);
  return (
    <>
      <PersonalInfoWrapper $isMobile={isMobile}>
        <Wrapper $gap={isMobile ? '0rem' : '1.563rem'} $isMobile={isMobile}>
          <TextBlock
            text={`${templatePersonalInformation?.placeHolder?.text || personalInformation.placeHolder.text} ${loanApplication?.borrowers?.[0].borrowerFirstName || ''} ${loanApplication?.borrowers?.[0].borrowerLastName || ''}`}
            direction={'flex-start'}
            color="default"
            type={TypeOfText.H2}
            isHidden={templatePersonalInformation?.placeHolder?.isHidden || false}
          />
          <FormGroupComponent data-testid="address-form" gap="1.563rem" onChange={handleChange}>
            <DropDownInput
              dataTestId="selectStatus"
              label={templatePersonalInformation?.formInputs?.selectStatus?.label || personalInformation.formInputs.selectStatus.label}
              placeHolder={templatePersonalInformation?.formInputs?.selectStatus?.placeHolder || personalInformation.formInputs.selectStatus.placeHolder}
              options={templatePersonalInformation?.formInputs?.selectStatus?.options}
              size="23%"
              value={formValues.selectStatus}
              typeOfIcon="person"
              labelContainer={true}
              hasAsterisk={templatePersonalInformation?.formInputs?.selectStatus?.isRequired || false}
              propertyType={PropertyType.Status}
              isHidden={templatePersonalInformation?.formInputs?.selectStatus?.isHidden}
              isInfoModal={true}
              infoModal={templatePersonalInformation?.modals?.maritalStatusInfo}
            />
            <DataPicker
              dataTestId="dateOfBirth"
              label={templatePersonalInformation?.formInputs?.dateBirth?.label || personalInformation.formInputs.dateBirth.label}
              labelContainer={true}
              placeholder={templatePersonalInformation?.formInputs?.dateBirth?.placeHolder || personalInformation.formInputs.dateBirth.placeHolder}
              typeOfIcon="date"
              propertyName={'dateOfBirth'}
              propertyType={PropertyType.DateBirthday}
              hasAsterisk={templatePersonalInformation?.formInputs?.dateBirth?.isRequired || false}
              isRequired={templatePersonalInformation?.formInputs?.dateBirth?.isRequired || false}
              value={loanApplication?.borrowers[0]?.borrowerDOB || ''}
              isHidden={templatePersonalInformation?.formInputs?.dateBirth?.isHidden ?? false}
              setIsValidDateOfBirth={setIsValidDateOfBirth}
              setIsErrorForm={setDateOfBirthError}
              isSentitiveInfo={true}
            />
            <TextInput
              dataTestId="SSN"
              label={templatePersonalInformation?.formInputs?.SSN?.label || personalInformation.formInputs.SSN.label}
              placeholder={templatePersonalInformation?.formInputs?.SSN?.placeHolder || personalInformation.formInputs.SSN.placeHolder}
              size="25%"
              typeOfIcon="text"
              isInfoModal={false}
              hasAsterisk={true}
              isRequired={true}
              propertyType={PropertyType.SSN}
              value={loanApplication?.borrowers[0]?.borrowerSSN || ''}
              isHidden={templatePersonalInformation?.formInputs?.SSN?.isHidden ?? false}
              type="password"
              onChange={(value) => handleSSNChange(value.toString(), PropertyType.SSN)}
              validationMessage={(() => {
                const hasError = formValues.SSN && !validateSSN(formValues.SSN);
                return hasError ? 'SSN must be a complete 9-digit number' : undefined;
              })()}
              isSentitiveInfo={true}
              onFocus={() => {
                if (loanApplication?.borrowers?.[0]?.borrowerSSN && !hasUserConfirmedSSNChange) {
                  setShowWarningSSNModal(true);
                }
              }}
            />
            <ConfirmMessageContainer $isHidden={templatePersonalInformation?.messages?.ssnMessages?.isHidden ?? false}>
              <TextBlock
                text={templatePersonalInformation?.messages?.ssnMessages?.title}
                type={TypeOfText.H1}
                color="textPrimary"
                isHidden={templatePersonalInformation?.messages?.ssnMessages?.isHidden}
              />
              <TextBlock
                text={templatePersonalInformation?.messages?.ssnMessages?.body}
                type={TypeOfText.H1}
                isHidden={templatePersonalInformation?.messages?.ssnMessages?.isHidden}
              />
            </ConfirmMessageContainer>
            <PhoneInput
              dataTestId="mobilePhone"
              placeHolder={templatePersonalInformation?.formInputs?.mobilePhone?.placeHolder || personalInformation.formInputs.mobilePhone.placeHolder}
              labelContainer={true}
              label={templatePersonalInformation?.formInputs?.mobilePhone?.label || personalInformation.formInputs.mobilePhone.label}
              size={'30%'}
              type="mobilePhone"
              value={formValues.mobilePhone}
              options={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.phone?.options}
              propertyName={'personal-information'}
              hasAsterisk={templatePersonalInformation?.formInputs?.mobilePhone?.isRequired ? true : handleIsRequired('cell', 'borrower')}
              isRequired={templatePersonalInformation?.formInputs?.mobilePhone?.isRequired ? true : handleIsRequired('cell', 'borrower')}
              propertyType={PropertyType.MobilePhone}
              isHidden={templatePersonalInformation?.formInputs?.mobilePhone?.isHidden ?? false}
              setIsErrorForm={setPhoneError}
              isSentitiveInfo={true}
            />
            <PhoneInput
              dataTestId="homePhone"
              placeHolder={templatePersonalInformation?.formInputs?.homePhone?.placeHolder || personalInformation.formInputs.homePhone.placeHolder}
              labelContainer={true}
              label={templatePersonalInformation?.formInputs?.homePhone?.label || personalInformation.formInputs.homePhone.label}
              size={'30%'}
              type="homePhone"
              value={formValues.homePhone}
              options={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.phone?.options}
              isHidden={templatePersonalInformation?.formInputs?.homePhone?.isHidden ?? false}
              propertyName={'personal-information'}
              hasAsterisk={templatePersonalInformation?.formInputs?.homePhone?.isRequired ? true : handleIsRequired('home', 'borrower')}
              isRequired={templatePersonalInformation?.formInputs?.homePhone?.isRequired ? true : handleIsRequired('home', 'borrower')}
              propertyType={PropertyType.HomePhone}
              setIsErrorForm={setPhoneError}
              isSentitiveInfo={true}
            />
            <PhoneInput
              dataTestId="workPhone"
              placeHolder={templatePersonalInformation?.formInputs?.workPhone?.placeHolder || personalInformation.formInputs.workPhone.placeHolder}
              labelContainer={true}
              label={templatePersonalInformation?.formInputs?.workPhone?.label || personalInformation.formInputs.workPhone.label}
              size={'30%'}
              type="workPhone"
              value={formValues.workPhone}
              options={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.phone?.options}
              isHidden={templatePersonalInformation?.formInputs?.workPhone?.isHidden ?? false}
              propertyName={'personal-information'}
              isRequired={templatePersonalInformation?.formInputs?.workPhone?.isRequired ? true : handleIsRequired('work', 'borrower')}
              hasAsterisk={templatePersonalInformation?.formInputs?.workPhone?.isRequired ? true : handleIsRequired('work', 'borrower')}
              propertyType={PropertyType.WorkPhone}
              setIsErrorForm={setPhoneError}
              isSentitiveInfo={true}
            />
            <DropDownInput
              dataTestId="contactMethod"
              label={templatePersonalInformation?.formInputs?.contactMethod?.label || personalInformation.formInputs.contactMethod.label}
              options={templatePersonalInformation?.formInputs?.contactMethod?.options}
              size="28%"
              value={formValues.contactMethod}
              placeHolder={templatePersonalInformation?.formInputs?.contactMethod?.placeHolder || personalInformation.formInputs.contactMethod.placeHolder}
              labelContainer={true}
              hasAsterisk={templatePersonalInformation?.formInputs?.contactMethod?.isRequired || true}
              isRequired={templatePersonalInformation?.formInputs?.contactMethod?.isRequired || true}
              propertyType={PropertyType.ContactMethod}
              isHidden={templatePersonalInformation?.formInputs?.contactMethod?.isHidden ?? false}
            />
            <DropDownInput
              dataTestId="citizenship"
              label={templatePersonalInformation?.formInputs?.citizenship?.label || personalInformation.formInputs.citizenship.label}
              options={templatePersonalInformation?.formInputs?.citizenship?.options}
              size="28%"
              placeHolder={templatePersonalInformation?.formInputs?.citizenship?.placeHolder || personalInformation.formInputs.citizenship.placeHolder}
              labelContainer={true}
              typeOfIcon="location"
              hasAsterisk={templatePersonalInformation?.formInputs?.citizenship?.isRequired || true}
              isRequired={templatePersonalInformation?.formInputs?.citizenship?.isRequired || true}
              capitalizeWord={false}
              propertyType={PropertyType.Citizenship}
              isHidden={templatePersonalInformation?.formInputs?.citizenship?.isHidden ?? false}
              value={loanApplication?.borrowers[0]?.borrowerCitizenship || ''}
            />
            <TextInput
              dataTestId="emailAddress"
              label={templatePersonalInformation?.formInputs?.emailAddress?.label || personalInformation.formInputs.emailAddress.label}
              placeholder={templatePersonalInformation?.formInputs?.emailAddress?.placeHolder || personalInformation.formInputs.emailAddress.placeHolder}
              size={'32%'}
              typeOfIcon="email"
              type="email"
              value={loanApplication?.borrowers[0]?.borrowerEmailAddress || ''}
              hasAsterisk={templatePersonalInformation?.formInputs?.emailAddress?.isRequired || false}
              isRequired={templatePersonalInformation?.formInputs?.emailAddress?.isRequired || false}
              propertyType={PropertyType.MailAddress}
              isHidden={templatePersonalInformation?.formInputs?.emailAddress?.isHidden || false}
            />
          </FormGroupComponent>
          {hasCoBorrower ? (
            <TextBlock
              text={templatePersonalInformation?.formInputs?.Coborrower?.title?.text?.replace('%%coApplicantLabel%%', coApplicantLabel) || 'Co-Borrower'}
              isHidden={templatePersonalInformation?.formInputs?.Coborrower?.title?.isHidden ?? false}
              direction={'flex-start'}
              color="default"
              type={TypeOfText.H2}
            />
          ) : (
            <></>
          )}
          {coBorrower < 1 ? (
            <ButtonWrapper $isHidden={templatePersonalInformation?.button?.add?.isHidden ?? false}>
              <Button
                label={templatePersonalInformation?.button?.add?.text?.replace('%%coApplicantLabel%%', coApplicantLabel) || personalInformation.button.add.text}
                hasIcon={true}
                onClick={() => setCoBorrower(coBorrower + 1)}
                data-testid="button"
                variant="primary-link"
                $alignRight="flex-end"
              />
            </ButtonWrapper>
          ) : (
            <ButtonWrapper $isHidden={templatePersonalInformation?.button?.remove?.isHidden ?? false}>
              <Button
                label={
                  //FIXME: enhance the span
                  <span>
                    <span>–</span>
                    <span className="text">
                      {templatePersonalInformation?.button?.remove?.text.replace('%%coApplicantLabel%%', coApplicantLabel) || personalInformation.button.remove.text}
                    </span>
                  </span>
                }
                onClick={() => setCoBorrower(coBorrower - 1)}
                data-testid="button"
                variant="primary-link"
                $alignRight="flex-end"
              />
            </ButtonWrapper>
          )}
          {coBorrower !== 0 &&
            Array.from({ length: coBorrower }).map((_, index) => (
              <FormGroupComponent key={index} onChange={handleChange}>
                <ConfirmMessageContainer $isHidden={templatePersonalInformation?.messages?.coBorrowerMessages?.isHidden ?? false}>
                  <TextBlock
                    text={templatePersonalInformation?.messages?.coBorrowerMessages?.title?.replace('%%coApplicantLabel%%', coApplicantLabel)}
                    type={TypeOfText.H1}
                    color="textPrimary"
                    isHidden={templatePersonalInformation?.messages?.coBorrowerMessages?.isHidden}
                  />
                  <TextBlock
                    text={templatePersonalInformation?.messages?.coBorrowerMessages?.body?.replace('%%coApplicantLabel%%', coApplicantLabel)}
                    type={TypeOfText.H1}
                    isHidden={templatePersonalInformation?.messages?.coBorrowerMessages?.isHidden}
                  />
                </ConfirmMessageContainer>
                <TextInput
                  dataTestId="firstNameCoBorrower"
                  label={templatePersonalInformation?.formInputs?.Coborrower?.firstName?.label || personalInformation.formInputs.Coborrower.firstName.label}
                  placeholder={templatePersonalInformation?.formInputs?.Coborrower?.firstName?.placeHolder || personalInformation.formInputs.Coborrower.firstName.placeHolder}
                  typeOfIcon="text"
                  value={loanApplication?.borrowers[index + 1]?.borrowerFirstName || ''}
                  size={'30%'}
                  propertyType={PropertyType.FirstNameCoBorrower}
                  isHidden={templatePersonalInformation?.formInputs?.Coborrower?.firstName?.isHidden ?? false}
                  isRequired={templatePersonalInformation?.formInputs?.Coborrower?.firstName?.isRequired || false}
                  hasAsterisk={templatePersonalInformation?.formInputs?.Coborrower?.firstName?.isRequired || false}
                />
                <TextInput
                  dataTestId="middleNameCoBorrower"
                  label={templatePersonalInformation?.formInputs?.Coborrower?.middleName?.label || personalInformation.formInputs.Coborrower.middleName.label}
                  placeholder={templatePersonalInformation?.formInputs?.Coborrower?.middleName?.placeHolder || personalInformation.formInputs.Coborrower.middleName.placeHolder}
                  typeOfIcon="text"
                  value={loanApplication?.borrowers[index + 1]?.borrowerMiddleName || ''}
                  size={'20%'}
                  isRequired={templatePersonalInformation?.formInputs?.Coborrower?.middleName?.isRequired || false}
                  propertyType={PropertyType.MiddleNameCoBorrower}
                  isHidden={templatePersonalInformation?.formInputs?.Coborrower?.middleName?.isHidden ?? false}
                />
                <TextInput
                  dataTestId="lastNameCoBorrower"
                  label={templatePersonalInformation?.formInputs?.Coborrower?.lastName?.label || personalInformation.formInputs.Coborrower.lastName.label}
                  placeholder={templatePersonalInformation?.formInputs?.Coborrower?.lastName?.placeHolder || personalInformation.formInputs.Coborrower.lastName.placeHolder}
                  typeOfIcon="text"
                  value={loanApplication?.borrowers[index + 1]?.borrowerLastName || ''}
                  size={'30%'}
                  propertyType={PropertyType.LastNameCoborrower}
                  isHidden={templatePersonalInformation?.formInputs?.Coborrower?.lastName?.isHidden ?? false}
                  isRequired={templatePersonalInformation?.formInputs?.Coborrower?.lastName?.isRequired || false}
                  hasAsterisk={templatePersonalInformation?.formInputs?.Coborrower?.lastName?.isRequired || false}
                />
                <DropDownInput
                  dataTestId="suffixCoBorrower"
                  label={templatePersonalInformation?.formInputs?.Coborrower?.suffix?.label || personalInformation.formInputs.Coborrower.suffix.label}
                  placeHolder={templatePersonalInformation?.formInputs?.Coborrower?.suffix?.title || personalInformation.formInputs.Coborrower.suffix.title}
                  options={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.suffix?.options}
                  isRequired={templatePersonalInformation?.formInputs?.Coborrower?.suffix?.isRequired || false}
                  value={loanApplication?.borrowers[index + 1]?.borrowerSuffixName || ''}
                  labelContainer={true}
                  size={'10%'}
                  propertyType={PropertyType.SuffixCoborrower}
                  isHidden={templatePersonalInformation?.formInputs?.Coborrower?.suffix?.isHidden || false}
                  capitalizeWord={false}
                />
                <DropDownInput
                  dataTestId="relationship"
                  labelContainer={true}
                  value={formValues.relationship || ''}
                  label={templatePersonalInformation?.formInputs?.Coborrower?.relationship?.label || personalInformation.formInputs.Coborrower.relationship.label}
                  placeHolder={templatePersonalInformation?.formInputs?.Coborrower?.relationship?.placeHolder || personalInformation.formInputs.Coborrower.relationship.placeHolder}
                  options={templatePersonalInformation?.formInputs?.Coborrower?.relationship?.options}
                  isRequired={templatePersonalInformation?.formInputs?.Coborrower?.relationship?.isRequired || false}
                  size={'23%'}
                  propertyType={PropertyType.Relationship}
                  isHidden={templatePersonalInformation?.formInputs?.Coborrower?.relationship?.isHidden ?? false}
                  hasAsterisk={templatePersonalInformation?.formInputs?.Coborrower?.relationship?.isRequired || false}
                />
                <DropDownInput
                  dataTestId="selectStatusCoborrower"
                  value={loanApplication?.borrowers[index + 1]?.borrowerMaritalStatus || ''}
                  label={templatePersonalInformation?.formInputs?.Coborrower?.selectStatus?.label || personalInformation.formInputs.Coborrower.selectStatus.label}
                  placeHolder={templatePersonalInformation?.formInputs?.Coborrower?.selectStatus?.placeHolder || personalInformation.formInputs.Coborrower.selectStatus.placeHolder}
                  options={templatePersonalInformation?.formInputs?.Coborrower?.selectStatus?.options}
                  size="25%"
                  typeOfIcon="person"
                  labelContainer={true}
                  propertyType={PropertyType.StatusCoBorrower}
                  isHidden={templatePersonalInformation?.formInputs?.Coborrower?.selectStatus?.isHidden ?? false}
                  isRequired={templatePersonalInformation?.formInputs?.Coborrower?.selectStatus?.isRequired || false}
                  hasAsterisk={templatePersonalInformation?.formInputs?.Coborrower?.selectStatus?.isRequired || false}
                  isInfoModal={true}
                  infoModal={templatePersonalInformation?.modals?.maritalStatusInfo}
                />
                <DataPicker
                  dataTestId="dateOfBirthCoBorrower"
                  label={templatePersonalInformation?.formInputs?.Coborrower?.dateBirth?.label || personalInformation.formInputs.Coborrower.dateBirth.label}
                  labelContainer={true}
                  placeholder={templatePersonalInformation?.formInputs?.Coborrower?.dateBirth?.placeHolder || personalInformation.formInputs.Coborrower.dateBirth.placeHolder}
                  typeOfIcon="date"
                  propertyName={'dateOfBirth'}
                  propertyType={PropertyType.DateBirthdayCoBorrower}
                  isHidden={templatePersonalInformation?.formInputs?.Coborrower?.dateBirth?.isHidden ?? false}
                  hasAsterisk={templatePersonalInformation?.formInputs?.Coborrower?.dateBirth?.isRequired || false}
                  isRequired={templatePersonalInformation?.formInputs?.Coborrower?.dateBirth?.isRequired || false}
                  value={loanApplication?.borrowers[1]?.borrowerDOB || ''}
                  setIsValidDateOfBirth={setIsValidDateOfBirth}
                  setIsErrorForm={setDateOfBirthCoBorrowerError}
                  isSentitiveInfo={true}
                />
                <PhoneInput
                  dataTestId="mobilePhoneCoBorrower"
                  value={loanApplication?.borrowers[index + 1]?.borrowerPrimaryPhone || ''}
                  placeHolder={templatePersonalInformation?.formInputs?.Coborrower?.mobilePhone?.placeHolder || personalInformation.formInputs.Coborrower.mobilePhone.placeHolder}
                  labelContainer={true}
                  label={templatePersonalInformation?.formInputs?.Coborrower?.mobilePhone?.label || personalInformation.formInputs.Coborrower.mobilePhone.label}
                  size={'24%'}
                  type="borrowerPrimaryPhone"
                  options={
                    templateConfig?.pages?.LetsGetStarted?.consumerInformation?.phone?.options as [
                      {
                        label: string;
                        beValue: string;
                      },
                    ]
                  }
                  isHidden={templatePersonalInformation?.formInputs?.Coborrower?.mobilePhone?.isHidden ?? false}
                  propertyName={'personal-information'}
                  hasAsterisk={templatePersonalInformation?.formInputs?.Coborrower?.mobilePhone?.isRequired ? true : handleIsRequired('cell', 'coBorrower')}
                  isRequired={templatePersonalInformation?.formInputs?.Coborrower?.mobilePhone?.isRequired ? true : handleIsRequired('cell', 'coBorrower')}
                  propertyType={PropertyType.MobilePhoneCoBorrower}
                  isSentitiveInfo={true}
                />
                <PhoneInput
                  dataTestId="homePhoneCoBorrower"
                  placeHolder={templatePersonalInformation?.formInputs?.Coborrower?.homePhone?.placeHolder || personalInformation.formInputs.Coborrower.homePhone.placeHolder}
                  labelContainer={true}
                  label={templatePersonalInformation?.formInputs?.Coborrower?.homePhone?.label || personalInformation.formInputs.Coborrower.homePhone.label}
                  size={'28%'}
                  type="borrowerAlternatePhone"
                  value={loanApplication?.borrowers[index + 1]?.borrowerAlternatePhone || ''}
                  options={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.phone?.options}
                  isHidden={templatePersonalInformation?.formInputs?.Coborrower?.homePhone?.isHidden ?? false}
                  propertyName={'personal-information'}
                  hasAsterisk={handleIsRequired('home', 'coBorrower')}
                  isRequired={handleIsRequired('home', 'coBorrower')}
                  propertyType={PropertyType.HomePhoneCoBorrower}
                  isSentitiveInfo={true}
                />
                <PhoneInput
                  dataTestId="workPhoneCoBorrower"
                  placeHolder={templatePersonalInformation?.formInputs?.Coborrower?.workPhone?.placeHolder || personalInformation.formInputs.Coborrower.workPhone.placeHolder}
                  labelContainer={true}
                  label={templatePersonalInformation?.formInputs?.Coborrower?.workPhone?.label || personalInformation.formInputs.Coborrower.workPhone.label}
                  size={'30%'}
                  type="borrowerWorkPhone"
                  value={loanApplication?.borrowers[index + 1]?.borrowerWorkPhone || ''}
                  options={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.phone?.options}
                  isHidden={templatePersonalInformation?.formInputs?.Coborrower?.workPhone?.isHidden ?? false}
                  propertyName={'personal-information'}
                  hasAsterisk={handleIsRequired('work', 'coBorrower')}
                  isRequired={handleIsRequired('work', 'coBorrower')}
                  propertyType={PropertyType.WorkPhoneCoBorrower}
                  isSentitiveInfo={true}
                />
                <TextInput
                  dataTestId="emailCoborrower"
                  label={templatePersonalInformation?.formInputs?.Coborrower?.emailAddress?.label || personalInformation.formInputs.Coborrower.emailAddress.label}
                  placeholder={templatePersonalInformation?.formInputs?.Coborrower?.emailAddress?.placeHolder || personalInformation.formInputs.Coborrower.emailAddress.placeHolder}
                  size={'32%'}
                  value={loanApplication?.borrowers[index + 1]?.borrowerEmailAddress || ''}
                  typeOfIcon="email"
                  type="email"
                  hasAsterisk={templatePersonalInformation?.formInputs?.Coborrower?.emailAddress?.isRequired || false}
                  propertyType={PropertyType.EmailCoborrower}
                  isHidden={templatePersonalInformation?.formInputs?.Coborrower?.emailAddress?.isHidden ?? false}
                  isRequired={templatePersonalInformation?.formInputs?.Coborrower?.emailAddress?.isRequired || false}
                />
                <DropDownInput
                  dataTestId="contactMethodCoBorrower"
                  label={templatePersonalInformation?.formInputs?.Coborrower?.contactMethod?.label || personalInformation.formInputs.Coborrower.contactMethod.label}
                  options={templatePersonalInformation?.formInputs?.Coborrower?.contactMethod?.options}
                  size="23%"
                  value={loanApplication?.borrowers[index + 1]?.borrowerPreferredContactMethod || ''}
                  placeHolder={
                    templatePersonalInformation?.formInputs?.Coborrower?.contactMethod?.placeHolder || personalInformation.formInputs.Coborrower.contactMethod.placeHolder
                  }
                  labelContainer={true}
                  propertyType={PropertyType.ContactMethodCoBorrower}
                  isHidden={templatePersonalInformation?.formInputs?.Coborrower?.contactMethod?.isHidden ?? false}
                  isRequired={templatePersonalInformation?.formInputs?.Coborrower?.contactMethod?.isRequired || true}
                  hasAsterisk={templatePersonalInformation?.formInputs?.Coborrower?.contactMethod?.isRequired || true}
                />
                <DropDownInput
                  dataTestId="citizenshipCoborrower"
                  label={templatePersonalInformation?.formInputs?.Coborrower?.citizenship?.label || personalInformation.formInputs.Coborrower.citizenship.label}
                  options={templatePersonalInformation?.formInputs?.Coborrower?.citizenship?.options || personalInformation.formInputs.Coborrower.citizenship.options || []}
                  size="28%"
                  value={formValues.citizenshipCoborrower}
                  onChange={(option: string, propertyType?: string) => handleChange(option, propertyType || PropertyType.CitizenshipCoborrower)}
                  placeHolder={templatePersonalInformation?.formInputs?.Coborrower?.citizenship?.placeHolder || personalInformation.formInputs.Coborrower.citizenship.placeHolder}
                  labelContainer={true}
                  typeOfIcon="location"
                  hasAsterisk={templatePersonalInformation?.formInputs?.Coborrower?.citizenship?.isRequired || true}
                  isRequired={templatePersonalInformation?.formInputs?.Coborrower?.citizenship?.isRequired || true}
                  propertyType={PropertyType.CitizenshipCoborrower}
                  isHidden={templatePersonalInformation?.formInputs?.Coborrower?.citizenship?.isHidden ?? false}
                />
                <TextInput
                  dataTestId="SSNCoBorrower"
                  label={templatePersonalInformation?.formInputs?.Coborrower?.SSN?.label || personalInformation.formInputs.Coborrower.SSN.label}
                  placeholder={templatePersonalInformation?.formInputs?.Coborrower?.SSN?.placeHolder || personalInformation.formInputs.Coborrower.SSN.placeHolder}
                  size="30%"
                  typeOfIcon="text"
                  hasAsterisk={templatePersonalInformation?.formInputs?.Coborrower?.SSN?.isRequired || true}
                  isRequired={templatePersonalInformation?.formInputs?.Coborrower?.SSN?.isRequired || true}
                  propertyType={PropertyType.SSNCoBorrower}
                  value={loanApplication?.borrowers[index + 1]?.borrowerSSN || ''}
                  isInfoModal={false}
                  isHidden={templatePersonalInformation?.formInputs?.Coborrower?.SSN?.isHidden ?? false}
                  type="password"
                  onChange={(value) => handleSSNChange(value.toString(), PropertyType.SSNCoBorrower)}
                  validationMessage={(() => {
                    const hasError = formValues.SSNCoBorrower && !validateSSN(formValues.SSNCoBorrower);
                    return hasError ? 'SSN must be a complete 9-digit number' : undefined;
                  })()}
                  isSentitiveInfo={true}
                />
                {coBorrower !== 0 && (
                  <CheckConsent
                    text={templateConfig?.pages?.LetsGetStarted?.eConsent?.text || pages?.LetsGetStarted?.eConsent?.text}
                    infoModal={templateConfig?.pages?.LetsGetStarted?.modals?.infoEconsent || pages?.LetsGetStarted?.modals?.infoEconsent}
                    isInfoModal={templateConfig?.pages?.LetsGetStarted?.modals?.infoEconsent?.isInfoModal || false}
                    direction={'center'}
                    isUnderline={false}
                    checked={loanApplication?.borrowers[index + 1]?.isSpouse || false}
                    onChange={handleChange}
                    type={'coapplicantConsent'}
                    isHidden={templateConfig?.pages?.LetsGetStarted?.eConsent?.isHidden ?? false}
                  />
                )}
              </FormGroupComponent>
            ))}
        </Wrapper>
        <Wrapper $minHeight="0" $gap="1rem" $padding="1.25rem 0.938rem" $isMobile={isMobile} $isHidden={templatePersonalInformation?.checkConsent?.isHidden || false}>
          {isMobile && (
            <TextBlock
              text={templateConfig?.pages?.LetsGetStarted?.typeOfInformation?.consent?.text || pages.LetsGetStarted.typeOfInformation.consent.text}
              direction={'flex-start'}
              color="default"
              type={TypeOfText.H2}
            />
          )}
          <CheckConsent
            text={templatePersonalInformation?.checkConsent?.text || personalInformation.checkConsent.text}
            isInfoModal={false}
            direction={'center'}
            isUnderline={false}
            onChange={handleChange}
            type={'TCPA'}
            isHidden={templatePersonalInformation?.checkConsent?.isHidden || false}
          />
        </Wrapper>
      </PersonalInfoWrapper>
      {showWarningSSNModal && (
        <ConfirmationModal
          openModal={showWarningSSNModal}
          setOpenModal={setShowWarningSSNModal}
          handleModalClose={handleBackModalResponse}
          infoModal={{
            body: 'Please be aware that changes to your application could impact your eligibility. Are you sure you want to continue?',
            acceptButton: 'Yes, change SSN',
            button: 'Cancel',
          }}
        />
      )}
      {showBackAfirmationModal && (
        <ConfirmationModal
          openModal={showBackAfirmationModal}
          setOpenModal={setShowBackAfirmationModal}
          handleModalClose={handleBackResponse}
          infoModal={{
            header: 'Warning!',
            body: 'Going back may affect your View My Offer pricing eligibility. Are you sure you want to continue?',
            acceptButton: 'Yes, go back',
            button: 'Cancel',
          }}
        />
      )}
      <NavigationButton
        variant="primary"
        nextButton={templatePersonalInformation?.NavigationButton?.nextButton || personalInformation.NavigationButton.nextButton}
        isBackButton={templatePersonalInformation?.NavigationButton?.backButton?.isHidden || false}
        backButton={templatePersonalInformation?.NavigationButton?.backButton?.text || personalInformation.NavigationButton.backButton.text}
        disabled={(() => {
          const isDisabled =
            formError ||
            !isValidDateOfBirth ||
            !isSSNValid ||
            dateOfBirthError ||
            phoneError ||
            SNNError ||
            dateOfBirthCoBorrowerError ||
            emailError ||
            (!coApplicantConsent && hasCoBorrower);

          return isDisabled;
        })()}
        type="next"
        onAccess={handleNavigation}
        navigation={navigate}
        pageOrigin={previousPage}
        currentPage={PageOrigin.PERSONAL_INFORMATION}
        destinationPage={PageOrigin.EMPLOYMENT_INFORMATION}
        hasDataChanged={hasDataChanged}
        resetChangeDetection={resetChangeDetection}
        onBackClick={() => setShowBackAfirmationModal(true)}
      />
    </>
  );
};

export default PersonalInformation;
